<script setup lang="ts">
import type { NuxtError } from "#app";

const { t } = useI18n();

const props = defineProps({
  error: {
    type: Object as () => NuxtError,
    default: () => null,
  },
  embedded: {
    type: Boolean,
    default: false,
  },
});

/**
 * Fix for related issue: https://github.com/nuxt/nuxt/issues/34579
 */
setTimeout(() => {
  const activeHead = useHead({
    title: `${props.error?.statusCode}  -  ${t("errors.pages.404.title")}`,
  });

  if (!props.embedded) {
    const head = useLocaleHead({ dir: true, seo: true, lang: true });
    activeHead.patch({
      htmlAttrs: {
        lang: head.value.htmlAttrs?.lang ?? "en",
        dir: <"ltr" | "rtl">head.value.htmlAttrs?.dir ?? "ltr",
        style: `--direction: ${head.value.htmlAttrs?.dir ?? "ltr"};`,
      },
      title: `${props.error?.statusCode}  -  ${t("errors.pages.404.title")}`,
    });
  }
}, 0);

const config = useRuntimeConfig();

const router = useRouter();

function handleBack() {
  clearError({ redirect: "/" });
}
</script>

<template>
  <component
    :is="embedded ? 'div' : 'main'"
    class="flex flex-col justify-center items-center gap-14 bg-ngray-7 rounded-lg p-4"
    :class="{ 'h-screen': !embedded }"
  >
    <img src="~/assets/images/TEMPLogo_BLUE.png" class="w-64 blue-logo" :alt="$t('alt.logo')" />
    <section
      class="flex flex-col items-center justify-center border-2 border-ngray-6 rounded-lg p-4 min-w-[420px] min-h-[491px] gap-4 bg-ngray-8"
    >
      <component :is="embedded ? 'h2' : 'h1'" class="text-nred-3 text-[115px] font-extrabold leading-none">{{
        error?.statusCode
      }}</component>

      <component :is="embedded ? 'h3' : 'h2'" class="text-nblue-1 text-lg font-bold tracking-[5px] leading-[25px]">
        {{ $t("errors.pages.404.title") }}
      </component>

      <p v-if="error?.statusCode === 404" class="text-sm font-normal text-nblue-1 w-[230px] text-center">
        {{ $t("errors.pages.404.message") }}
      </p>

      <p v-else class="text-sm font-normal text-nblue-1 w-[230px] text-center">
        {{ $t("errors.pages.404.unexpectedError") }}
      </p>

      <button
        class="text-ngray-8 bg-nblue-2 rounded-full px-6 py-3 font-semibold text-center"
        type="button"
        @click="handleBack"
      >
        {{ $t("buttons.returnHome") }}
      </button>

      <display-version theme="blue" />
      <slot />
    </section>
  </component>
</template>
